import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Trash2, Plus } from "lucide-react";
import { Input } from "./Input";
import { FormGroup } from "./FormGroup";
import { FormLabel } from "./FormLabel";
import { SelectInput } from "./SelectInput";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";

const FieldsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
  align-items: center;
`;

const CurrencyField = styled.div`
  display: flex;
  gap: ${spacing.x1}px;
  align-items: center;
`;

const AmountInput = styled(Input)`
  width: 140px;
`;

const CurrencySelect = styled(SelectInput)`
  width: 100px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: ${spacing.x05}px;
  border-radius: 4px;
  min-width: auto;

  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.text};
  }
`;

const RemoveButton = styled(IconButton)`
  &:hover {
    color: ${colors.danger};
  }
`;

const AddButton = styled(IconButton)`
  margin-left: auto;
`;

const Label = styled(FormLabel)`
  font-weight: 600;
  margin-bottom: ${spacing.x1}px;
`;

interface MultiCurrencyMoneyInputProps {
  label: string;
  value: MultiCurrencyMoney;
  onChange: (value: MultiCurrencyMoney) => void;
  amountPlaceholder?: string;
}

const currenciesFromMoney = (value: MultiCurrencyMoney): Currency[] => {
  const existing = Object.values(Currency).filter(
    (currency) => value.currencies[currency] != null
  );
  if (existing.length === 0) {
    return [Currency.SEK];
  }
  if (!existing.includes(Currency.SEK)) {
    return existing;
  }
  return [
    Currency.SEK,
    ...existing.filter((currency) => currency !== Currency.SEK),
  ];
};

export const MultiCurrencyMoneyInput = ({
  label,
  value,
  onChange,
  amountPlaceholder,
}: MultiCurrencyMoneyInputProps) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Currency[]>(() => currenciesFromMoney(value));

  const availableCurrencies = Object.values(Currency).filter(
    (currency) => !rows.includes(currency)
  );

  const handleAmountChange = (currency: Currency, amountKr: string) => {
    const amountCents = Math.round((parseFloat(amountKr) || 0) * 100);
    onChange({
      currencies: {
        ...value.currencies,
        [currency]: {
          amount: amountCents,
          currency,
        },
      },
    });
  };

  const handleCurrencyChange = (index: number, nextCurrency: Currency) => {
    const previousCurrency = rows[index];
    if (previousCurrency === nextCurrency) return;

    setRows((current) =>
      current.map((currency, rowIndex) =>
        rowIndex === index ? nextCurrency : currency
      )
    );

    const previousMoney = value.currencies[previousCurrency];
    const nextCurrencies = { ...value.currencies };
    delete nextCurrencies[previousCurrency];
    if (previousMoney != null) {
      nextCurrencies[nextCurrency] = {
        amount: previousMoney.amount,
        currency: nextCurrency,
      };
    }
    onChange({ currencies: nextCurrencies });
  };

  const handleAddCurrency = () => {
    const nextCurrency = availableCurrencies[0];
    if (!nextCurrency) return;
    setRows((current) => [...current, nextCurrency]);
  };

  const handleRemoveCurrency = (index: number) => {
    const currency = rows[index];
    setRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
    if (value.currencies[currency] == null) return;
    const nextCurrencies = { ...value.currencies };
    delete nextCurrencies[currency];
    onChange({ currencies: nextCurrencies });
  };

  return (
    <FormGroup>
      <Label>{label}</Label>
      <FieldsRow>
        {rows.map((currency, index) => {
          const currencyOptions = [currency, ...availableCurrencies];
          return (
            <CurrencyField key={index}>
              <AmountInput
                type="number"
                step="0.01"
                min="0"
                placeholder={amountPlaceholder}
                value={
                  value.currencies[currency]?.amount != null
                    ? value.currencies[currency].amount / 100
                    : ""
                }
                onChange={(e) => handleAmountChange(currency, e.target.value)}
              />
              <CurrencySelect
                value={currency}
                onChange={(e) =>
                  handleCurrencyChange(index, e.target.value as Currency)
                }
              >
                {currencyOptions.map((currencyOption) => (
                  <option key={currencyOption} value={currencyOption}>
                    {currencyOption}
                  </option>
                ))}
              </CurrencySelect>
              {rows.length > 1 && (
                <RemoveButton
                  type="button"
                  aria-label={t("common.remove")}
                  onClick={() => handleRemoveCurrency(index)}
                >
                  <Trash2 size={16} />
                </RemoveButton>
              )}
            </CurrencyField>
          );
        })}
        {availableCurrencies.length > 0 && (
          <AddButton
            type="button"
            aria-label={t("multiCurrencyMoneyInput.addCurrency")}
            onClick={handleAddCurrency}
          >
            <Plus size={16} />
          </AddButton>
        )}
      </FieldsRow>
    </FormGroup>
  );
};
